/**
 * ClientProfileContext — carrega o perfil da instalação no arranque e expõe
 * helpers de módulos/features/nomenclatura a toda a app.
 *
 * Carregado de GET /config/profile (público, antes do login, para aplicar
 * branding já na página de login). Enquanto não responde, usa DEFAULT_PROFILE.
 *
 * Ver MULTI_CLIENTE.md.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { api } from "@/lib/api-typed";
import {
  DEFAULT_PROFILE,
  sectionToModule,
  type ClientProfile,
  type FeatureId,
} from "@/lib/client-profile";

interface ClientProfileContextType {
  profile: ClientProfile;
  isLoading: boolean;
  /** Módulo canónico ativo? (core está sempre ligado) */
  hasModule: (moduleId: string) => boolean;
  /** Secção de menu (menu-paths.ts) ativa? Resolve o id para módulo. */
  hasModuleForSection: (sectionId: string) => boolean;
  /** Feature extra ligada? (ex. "hr.advanced_export") */
  hasFeature: (featureKey: FeatureId) => boolean;
  /** Nomenclatura: substituto do cliente ou o label canónico. */
  t: (label: string) => string;
}

const ClientProfileContext = createContext<ClientProfileContextType | undefined>(
  undefined
);

export const ClientProfileProvider = ({ children }: { children: ReactNode }) => {
  const [profile, setProfile] = useState<ClientProfile>(DEFAULT_PROFILE);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get("/config/profile");
        if (!cancelled) setProfile(data);
      } catch {
        // Falha de rede → fica o DEFAULT_PROFILE (core neutro). A app arranca na mesma.
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const contextValue = useMemo<ClientProfileContextType>(() => {
    const modules = new Set(profile.modules);
    const features = new Set(profile.features);
    return {
      profile,
      isLoading,
      hasModule: (moduleId: string) => modules.has(moduleId),
      hasModuleForSection: (sectionId: string) =>
        modules.has(sectionToModule(sectionId)),
      hasFeature: (featureKey: FeatureId) => features.has(featureKey),
      t: (label: string) => profile.nomenclature[label] ?? label,
    };
  }, [profile, isLoading]);

  return (
    <ClientProfileContext value={contextValue}>
      {children}
    </ClientProfileContext>
  );
};

export const useClientProfile = (): ClientProfileContextType => {
  const context = useContext(ClientProfileContext);
  if (!context) {
    throw new Error(
      "useClientProfile must be used within a ClientProfileProvider"
    );
  }
  return context;
};
